import { useEffect, useState, useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';

import type { OrderItem } from '../../../graphql/query/getOrder';
import { filterItemsByEligibility } from '../../../utils/item';
import { rawString } from '../../Language/Language';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';
import { useShipmentMetadata } from '../../../hooks/useShipmentGroups';
import { useWorkflow } from '../../../workflow/hooks';
import type { TaskComponentType } from '../../../workflow/schemaTypes';
import { useToast } from '@repo/ui/Toast/ToastRoot';
import { EligibleItemsView } from './EligibleItemsView';
import { showMoreCutoff } from './ShowMoreButton';
import {
  sortItemsByConfig,
  DEFAULT_ITEM_SELECT_CONFIG,
} from '../../../utils/itemSelectConfig';
import type { ItemSelectConfig } from '../../../graphql/query/getRetailerSettings';

export const EligibleItems: TaskComponentType = ({ handleNext }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { order, retailerSettings, retailer } = useWorkflow();
  const { errorToast } = useToast();
  const { shipmentGroups, hasMultipleShipments } = useShipmentMetadata();
  const { items, orderNumber } = order ?? { items: [] };
  const { setValue } = useFormContext();

  // Get item-select-config from retailer settings, fallback to default
  const itemSelectConfig: ItemSelectConfig =
    (retailerSettings?.features?.itemSelectConfig as
      | ItemSelectConfig
      | undefined) ?? DEFAULT_ITEM_SELECT_CONFIG;

  // Filter eligible items (memoized to prevent unnecessary recalculations)
  // Also filter out items with no remaining quantity (all claimed)
  const eligibleItems = useMemo(
    () =>
      (filterItemsByEligibility(items as OrderItem[], true) ?? []).filter(
        (item) => (item.quantity?.remaining ?? 0) > 0,
      ),
    [items],
  );

  // Apply sorting based on config
  const sortedEligibleItems = useMemo(
    () => sortItemsByConfig(eligibleItems, itemSelectConfig),
    [eligibleItems, itemSelectConfig],
  );

  const notCutoff = sortedEligibleItems.length <= showMoreCutoff;

  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    setShowMore(notCutoff);
  }, [notCutoff, setShowMore]);

  const handleClaimClick = useCallback(
    (claimId: string) => {
      navigate({
        to: '/$retailer/claim-details',
        params: { retailer: retailer ?? 's' },
        search: (prev) => ({
          ...prev,
          order: orderNumber,
          claimId,
        }),
      });
    },
    [navigate, retailer, orderNumber],
  );

  if (!eligibleItems || eligibleItems.length === 0) {
    return null;
  }

  const handleInitialSelect = async (
    itemId: string,
    trackingNumber?: string,
  ) => {
    if (!itemId) {
      return errorToast({
        message:
          rawString({
            langKey: 'pdx.item_selection.eligible.navigation_error',
          }) ?? '',
      });
    }

    // Set the newly selected item as the only item in itemSelection and items
    const newItem = {
      id: itemId,
      quantity: 1,
    };

    setValue('items', {
      [itemId]: {
        itemSelection: {
          ...newItem,
          ...(trackingNumber && { trackingNumber }),
        },
      },
    });

    handleNext();
  };

  return (
    <EligibleItemsView
      eligibleItems={sortedEligibleItems}
      hasMultipleShipments={hasMultipleShipments}
      shipmentGroups={shipmentGroups}
      isMobile={isMobile}
      showMore={showMore}
      onShowMore={() => setShowMore(true)}
      onItemSelect={handleInitialSelect}
      onClaimClick={handleClaimClick}
      itemSelectConfig={itemSelectConfig}
    />
  );
};
